import Select from "@/components/Select/Select";
import { useState } from "react";

interface Props {
  identifier: string[];
  unit?: string;
  numberFormat?: boolean;
  onChange: (key: string, value: string, isNumber: boolean) => void;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  steps?: string[];
  min?: string;
  max?: string;
  label?: string;
}

export const getRangeSteps = (start: number, end: number, step: number) => {
  const arrayLength = Math.floor((end - start) / step) + 1;
  const range = [...Array(arrayLength).keys()].map(
    (x) => `${x * step + start}`
  );
  return range;
};

const getFormatOptions = (unit) => {
  switch (unit) {
    case "km":
      return {
        style: "unit",
        unit: "kilometer",
      };
    case "€":
      return {
        style: "currency",
        currency: "EUR",
      };
    default:
      return {};
  }
};

const formatNumber = (
  number: string,
  unit: string,
  numberFormat: boolean = false
) => {
  if (!numberFormat) return `${number} ${unit}`;

  const options = getFormatOptions(unit);

  if (!Object.keys(options).length) {
    return `${new Intl.NumberFormat("de-DE").format(
      parseFloat(number)
    )} ${unit}`;
  }
  return new Intl.NumberFormat("de-DE", options).format(parseFloat(number));
};

export const Range = ({
  identifier,
  unit = "",
  numberFormat = false,
  onChange,
  className = "",
  steps = [],
  min: minProp,
  max: maxProp,
  label = "",
}: Props) => {
  const [minKey, maxKey] = identifier;
  const [open, setOpen] = useState(false);
  if (!steps) return null;
  const min = minProp || `${Math.min(...steps.map((el) => parseInt(el) || 0))}`;
  const max = maxProp || `${Math.max(...steps.map((el) => parseInt(el) || 0))}`;

  const allowEqualValues = unit === "" || unit === "PS";
  const handleChange = (val, type) => {
    switch (type) {
      case "min":
        onChange(minKey, val, false);
        break;
      case "max":
        onChange(maxKey, val, false);
        break;
    }
  };

  return (
    <div
      className={`lg:flex grid grid-cols-2 gap-6 ${className} ${
        open ? "isOpenRange" : ""
      }`}
    >
      <p className="col-span-2 font-bold flex lg:hidden">{label}</p>
      <Select
        className="w-full "
        value={min}
        onOpenChange={() => {
          setOpen(!open);
        }}
        onValueChange={(val) => handleChange(val, "min")}
      >
        {steps.map((item) => (
          <Select.Item
            key={item}
            value={`${item}`}
            disabled={
              allowEqualValues
                ? parseInt(item) > parseInt(max)
                : parseInt(item) >= parseInt(max)
            }
            itemClass="!w-full"
          >
            {formatNumber(item, unit, numberFormat)}
          </Select.Item>
        ))}
      </Select>
      <Select
        className={`!w-full ${open ? "isOpenRange" : ""}`}
        value={max}
        onOpenChange={() => {
          setOpen(!open);
        }}
        onValueChange={(val) => handleChange(val, "max")}
      >
        {steps.map((item) => (
          <Select.Item
            key={item}
            value={`${item}`}
            disabled={
              allowEqualValues
                ? parseInt(item) < parseInt(min)
                : parseInt(item) <= parseInt(min)
            }
            itemClass="!w-full"
          >
            {formatNumber(item, unit, numberFormat)}
          </Select.Item>
        ))}
      </Select>
    </div>
  );
};
